import { Loader2, Paperclip, Send } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useStore } from "@/store";
import { useChatStore } from "@/store/chat/chat.store";
import { useNavigate } from "@tanstack/react-router";
import { useShallow } from "zustand/react/shallow";

function normalizeTitle(message: string, maxLength = 60): string {
  const trimmed = message.trim().replace(/\s+/g, " ");
  if (!trimmed) return "Chat";
  if (trimmed.length <= maxLength) return trimmed;
  return trimmed.substring(0, maxLength - 3) + "...";
}

export type TSectionCardChatInputThreadProps = {
  mode: "thread";
  companyId: string;
  sectionId: string;
  /** Optional prefix for thread title (e.g. document name for readiness). */
  titlePrefix?: string;
  disabled?: boolean;
  placeholder?: string;
};

export type TSectionCardChatInputInvestorProps = {
  mode: "investorChat";
  investorCompanyId: string;
  sectionId: string;
  founderCompanyId: string;
  dealName: string;
  disabled?: boolean;
  placeholder?: string;
};

export type TSectionCardChatInputProps = TSectionCardChatInputThreadProps | TSectionCardChatInputInvestorProps;

export function SectionCardChatInput(props: TSectionCardChatInputProps) {
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (files.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [files.length]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    setFiles(fileList ? Array.from(fileList) : []);
  }, []);

  const placeholder = props.placeholder ?? "Ask question...";
  const disabled = props.disabled ?? false;

  const { createThread, openChatThreadSession, setThreadTitle } = useChatStore(
    useShallow((state) => ({
      createThread: state.createThread,
      openChatThreadSession: state.openChatThreadSession,
      setThreadTitle: state.setThreadTitle,
    }))
  );
  const addInvestorChatThread = useChatStore((s) => s.addInvestorChatThread);
  const fetchCompanies = useStore((state) => state.fetchCompanies);
  const companies = useStore((state) => state.companies);

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed || disabled || isBusy) return;

    if (props.mode === "thread") {
      setError(null);
      setIsBusy(true);
      try {
        const chatTitle = props.titlePrefix
          ? `${props.titlePrefix}: ${normalizeTitle(trimmed)}`
          : normalizeTitle(trimmed);
        const result = await createThread({
          company_id: props.companyId,
          section_id: props.sectionId,
          title: chatTitle,
        });

        if (!result.success || !result.thread) {
          setError(result.error ?? "Failed to create chat thread. Please try again.");
          setIsBusy(false);
          return;
        }

        const thread = result.thread;
        setThreadTitle(thread.id, thread.title ?? chatTitle);
        const company = companies?.find((c) => c.id === props.companyId);
        const section = company?.sections?.find((s) => s.id === props.sectionId);

        openChatThreadSession({
          threadId: thread.id,
          companyId: props.companyId,
          companyName: company?.name,
          sectionId: thread.section_id ?? props.sectionId,
          sectionName: section?.name ?? "Section",
          title: thread.title ?? chatTitle,
          initialPrompt: trimmed,
        });

        void fetchCompanies({ force: true });
        void navigate({
          to: "/app/companies/$company_id/sections/$section_id/threads/$thread_id",
          params: {
            company_id: thread.company_id,
            section_id: thread.section_id ?? props.sectionId,
            thread_id: thread.id,
          },
        });
        setMessage("");
      } catch {
        setError("Failed to create chat thread. Please try again.");
      } finally {
        setIsBusy(false);
      }
      return;
    }

    // mode === "investorChat"
    if (!props.investorCompanyId || !props.sectionId) return;
    setIsBusy(true);
    const threadId = crypto.randomUUID();
    const chatTitle = `${props.dealName}: ${normalizeTitle(trimmed)}`;

    addInvestorChatThread(props.investorCompanyId, props.sectionId, {
      id: threadId,
      title: chatTitle,
      founder_company_id: props.founderCompanyId,
      founder_name: props.dealName,
      created_at: new Date().toISOString(),
    });

    void navigate({
      to: "/app/companies/$company_id/sections/$section_id/investor-chat/$thread_id",
      params: {
        company_id: props.investorCompanyId,
        section_id: props.sectionId,
        thread_id: threadId,
      },
      search: {
        founder_company_id: props.founderCompanyId,
        founder_name: props.dealName,
        initial_prompt: trimmed,
      },
    });
    setMessage("");
    setIsBusy(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !disabled && !isBusy) void handleSend();
  };

  const canSend = !disabled && !isBusy && (message.trim().length > 0 || files.length > 0);

  return (
    <div className="relative mt-4 space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-muted-foreground disabled:opacity-50"
          disabled={disabled || isBusy}
          aria-label="Attach file"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          disabled={disabled || isBusy}
          onChange={handleFileChange}
        />
        <Input
          type="text"
          placeholder={placeholder}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || isBusy}
          className="min-w-0 flex-1"
        />
        <Button
          type="button"
          onClick={() => void handleSend()}
          disabled={!canSend}
          aria-label="Send question"
          className="h-8 flex-shrink-0 gap-2 rounded-lg bg-primary px-4 text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted"
        >
          {isBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Send className="h-4 w-4" />
              <span className="text-sm">Send</span>
            </>
          )}
        </Button>
      </div>
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800">{error}</div>
      ) : null}
    </div>
  );
}
